import {createApi} from '@reduxjs/toolkit/query/react';
import {CHG_ENDPOINT} from '../utils/constants';
import {authenticatedFetchBaseQuery, HttpMethod} from "./index.ts";
import {FullUser, UserProvider} from "../types/adminstate.ts";
import {BEUserStats, LoginInput, UserInfo, UserUpdateProfileData,} from "../types/userstate.ts";
import {SessionStats} from "../types/sessionsstate.ts";
import {useAuth0, User} from "@auth0/auth0-react";
import {currentLocation, navigate, Routes} from "../utils/router.tsx";
import {parse} from "qs";
import {paramsString, removeUndefinedOrNull, TransformedDataParams} from "../utils/commons.ts";
import {Paginated} from "@kopjra/uikit";
import {BEPaginated} from "../types";

export const userStoreApi = createApi({
    reducerPath: "userStoreApi",
    baseQuery: authenticatedFetchBaseQuery(CHG_ENDPOINT),
    endpoints: (builder) => ({
        resendEmail: builder.mutation<void, void>({
            query: () => ({
                url: "/users/current:sendVerificationEmail",
                method: "POST",
            }),
        }),
        resetPassword: builder.mutation<void, void>({
            query: () => ({
                url: "/users/current:resetPassword",
                method: "POST",
            }),
        }),
        updateUserProfile: builder.mutation<void, UserUpdateProfileData>({
            query: (up) => ({
                url: "/users/current",
                method: "PUT",
                body: up,
            }),
        }),
    }),
});

export const authUserApi = createApi({
    reducerPath: "authUserApi",
    baseQuery: authenticatedFetchBaseQuery(),
    tagTypes: ["UserInfo"],
    endpoints: (builder) => ({
        getUserInfo: builder.query<UserInfo | undefined, User | undefined>({
            serializeQueryArgs: ({queryArgs}) => {
                return queryArgs ? {id: queryArgs.sub} : {id: "no-auth0-user"};
            },
            queryFn: async (auth0User = {}, api, extraOptions, baseQuery) => {
                try {
                    const locatonQP = parse(currentLocation.search, {ignoreQueryPrefix: true});
                    const baseQueryIgnoreAuthError = authenticatedFetchBaseQuery(undefined, true);
                    const isAuthenticatedRes = await baseQueryIgnoreAuthError("/users/current/isAuthenticated", api, extraOptions);
                    if (isAuthenticatedRes.error) {
                        console.error("Error checking authentication:", isAuthenticatedRes.error);
                        return {data: undefined};
                    }
                    if (locatonQP?.ticket) {
                        navigate(currentLocation.pathname, {replace: true});
                    }
                    const currentUserResponse = await baseQuery("/users/current");
                    if (currentUserResponse.error) {
                        console.error("Error fetching current user:", currentUserResponse.error);
                        return {data: undefined};
                    }
                    const currentUser = currentUserResponse.data as UserInfo;
                    const userInfo: UserInfo = removeUndefinedOrNull({
                        ...currentUser,
                        provider: currentUser.provider || UserProvider.INTERNAL,
                        firstname: auth0User?.given_name || currentUser.firstname,
                        lastname: auth0User?.family_name || currentUser.lastname,
                        picture: auth0User?.picture || currentUser.picture,
                    });

                    const storeUrlResponse = await baseQuery("/users/current/storeUrl");
                    if (!storeUrlResponse.error) {
                        try {
                            const storeUrl = storeUrlResponse.data as {url: string};
                            userInfo.storeUrl = storeUrl.url;
                        } catch (err) {
                            console.error("Error parsing store URL:", err);
                        }
                    }

                    const profileUrlResponse = await baseQuery("/users/current/profileUrl");
                    if (!profileUrlResponse.error) {
                        try {
                            const profileUrl = profileUrlResponse.data as {url: string};
                            userInfo.profileUrl = profileUrl.url;
                        } catch (err) {
                            console.error("Error parsing store URL:", err);
                        }
                    }

                    return {data: userInfo};
                } catch {
                    return {data: undefined};
                }
            },
            providesTags: ["UserInfo"],
        }),
        login: builder.mutation<void, LoginInput>({
            queryFn: async ({username, password, from = "/sessions"}, api, extraOptions) => {
                const baseQueryIgnoreAuthError = authenticatedFetchBaseQuery(undefined, true);
                try {
                    const response = await baseQueryIgnoreAuthError({
                        url: "/login",
                        method: HttpMethod.POST,
                        body: {username, password}
                    }, api, extraOptions);
                    if (response.error) return {error: response.error};
                    navigate(from !== "/login" ? from || "/sessions" : "/sessions", {replace: true});
                    return {data: undefined};
                } catch {
                    return {error: {status: "CUSTOM_ERROR", error: "Login failed"}};
                }
            },
            invalidatesTags: ["UserInfo"],
        }),
        logout: builder.mutation<void, boolean | undefined>({
            queryFn: async (redirect = true, api, extraOptions, baseQuery) => {
                const response = await baseQuery({
                    url: "/logout",
                    method: HttpMethod.POST,
                });
                if (response.error) return {error: response.error};
                if (redirect) {
                    Routes.goLogin(true);
                }
                return {data: undefined};
            },
            invalidatesTags: ["UserInfo"],
        }),
        resetAuth: builder.mutation<void, void>({
            queryFn: async (_arg, api) => {
                api.dispatch(authUserApi.util.resetApiState());
                return {data: undefined};
            },
        }),
    })
});

function transformToUser(obj: FullUser): FullUser {
    return {
        userId: obj.userId,
        email: obj.email,
        firstname: obj.firstname,
        lastname: obj.lastname,
        provider: obj.provider || UserProvider.INTERNAL,
        consumedCredits: obj.consumedCredits || 0,
        crmid: obj.crmid || "",
        externalId: obj.externalId || "",
    };
}

export const infoUserApi = createApi({
    reducerPath: "infoUserApi",
    baseQuery: authenticatedFetchBaseQuery(),
    endpoints: (builder) => ({
        getUsers: builder.query<Paginated<FullUser>, TransformedDataParams>({
            query: (params) => `/users${paramsString(params)}`,
            transformResponse: (response: BEPaginated<FullUser>) => ({
                items: response.results.map((u: FullUser) => transformToUser(u)),
                total: response.total,
            }),
        }),
        getStats: builder.query<SessionStats, void>({
            query: () => ({
                url: "/users/current/statistics",
                method: "GET",
            }),
            transformResponse: (response: BEUserStats) => ({
                completed: response.sessions.closed,
                remaining: response.credits.available,
                used: response.credits.consumed,
                infinite: response.credits.infinite,
            }),
        }),
    })
});

export const { useResendEmailMutation, useResetPasswordMutation, useUpdateUserProfileMutation } = userStoreApi;
export const { useLoginMutation, useLogoutMutation, useResetAuthMutation } = authUserApi;
export const { useGetUsersQuery, useLazyGetUsersQuery, useGetStatsQuery } = infoUserApi;

export function useGetUserInfo() {
    const {user} = useAuth0();
    return authUserApi.useGetUserInfoQuery(user);
}